import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import type { Medical } from '~entities/student/modal/medical';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface GetClassPerformanceAnalyticsResponse {
  studentPerformance: Array<StudentPerformance>;
  studentDistribution: StudentDistribution;
  pagination: Pagination;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface StudentDistribution {
  'Excellent (90-100%)': number;
  'Good (75-89%)': number;
  'Average (60-74%)': number;
  'Needs Improvement (<60%)': number;
}

export interface StudentPerformance {
  name: string;
  classesAttended: number;
  classesDone: number;
  status: 'Needs Improvement' | 'Average' | 'Good' | 'Excellent';
  attendancePercentage: string;
  medical: Medical;
}

export interface GetClassPerformanceAnalyticsParams {
  enrollmentId: string;
  limit?: number;
  page?: number;
}

export const GetClassPerformanceAnalytics = async ({
  enrollmentId,
  page = 1,
  limit = 10,
}: GetClassPerformanceAnalyticsParams) => {
  const response = await baseAPI.get<GetClassPerformanceAnalyticsResponse>(
    `/students/attendance/performance/${enrollmentId}`,
    {
      params: {
        page,
        limit,
      },
    },
  );
  return response.data;
};

export const getClassPerformanceAnalyticsOptions = (
  params: GetClassPerformanceAnalyticsParams,
) =>
  queryOptions({
    queryKey: [KEYS.CLASS_PERFORMANCE, params],
    queryFn: () => GetClassPerformanceAnalytics(params),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });

export const useGetClassPerformanceAnalytics = (
  params: GetClassPerformanceAnalyticsParams,
) => {
  return useQuery(getClassPerformanceAnalyticsOptions(params));
};
